import { loadStripe } from "@stripe/stripe-js";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CertificateOfAuthenticityIcon from '@/assets/all-icons/certificate-of-authenticity-icon.jsx';
import stripeConfig, { resolveStripePublishableKey } from '@/config/stripe.config';
import useLimitedEditionShippingQuote from '@/hooks/useLimitedEditionShippingQuote';
import usePaymentProfile from '@/hooks/usePaymentProfile';
import usePreferredLocale from '@/hooks/usePreferredLocale';
import useTranslations from '@/i18n/useTranslations';
import useArtworkPurchasePayment from '@/pages/artwork/hooks/useArtworkPurchasePayment';
import { getLocalizedArtworkMedium } from '@/pages/artwork/utils/artworkMediumDisplay.utils';
import { saveArtworkPurchaseResume } from '@/pages/artwork/utils/artworkPurchaseResume.utils';
import { formatArtworkTitleForDisplay } from '@/pages/artwork/utils/artworkTitleDisplay.utils';
import {
  getArtworkPurchaseDetails,
  getArtworkPurchaseGalleryProfile,
  getArtworkShippingConfig,
} from '@/pages/artwork/utils/artworkView.selectors';
import {
  ARTWORK_PREFERENCE_SIGNALS,
  trackArtworkPreferenceInteraction,
} from '@/services/artworkPreferenceInteraction.service';
import UserService from '@/services/user.service';
import PurchasePaymentMethodPanel from '@/shared-components/artwork/buy-details/payment-methods/PurchasePaymentMethodPanel';
import {
  FRAME_OPTION_LABELS,
  MUSEUM_LABEL_OPTION_LABELS,
} from '@/shared-components/artwork/buy-details/select-size/constants';
import PersonalizedMessage from '@/shared-components/artwork/buy-details/shipping-details/components/PersonalizedMessage';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import FormError from '@/shared-components/error/components/FormError';
import BackArrowIcon from '@/shared-components/icons/components/public/BackArrowIcon';
import SUARTE_WORDMARK_DATA_URL from '@/shared-components/icons/components/public/suarteWordmarkDataUrl';
import PurchasePaymentModal from '@/shared-components/payment/PurchasePaymentModal';
import BillingDetailsCard from '@/shared-components/payment-profile/BillingDetailsCard';
import { getArtworkArtistDisplayName } from '@/utils/artworkArtists';
import { formatCurrencyAmount } from '@/utils/currency.utils';
import { resolveLocalizedPublicPath } from '@/utils/localePublicUrl';
import NumberUtils from '@/utils/number.utils';
import {
  calculateVatAmount,
  qualifiesForEuReverseCharge,
  resolveOriginalTaxContext,
  resolveVatRate,
  TAX_MODES,
} from '@/utils/vat.utils';

import { ArtworkOfferPrice } from '../../components/ArtworkOfferElements';
import PaymentDetails from '../../purchase/components/PaymentDetails';
import PopupCertificateOfAuthenticity from '../../purchase/components/popup-certificate-of-authenticity';
import PopupCertificateOfOwnership from '../../purchase/components/popup-certificate-of-ownership';
import SelectSize from '../../purchase/select-size';
import ShippingDetails from '../../purchase/shipping-details';
import { parseOfferAmount, resolveActiveViewerOffer } from '../../utils/offer.utils';

import '../../purchase/purchase-process.css';
import './styles/BuyDetailsDesktop.css';
const PROFILE_ERROR_CODES = new Set([
  "BIDDER_PROFILE_INCOMPLETE",
  "MISSING_CARDHOLDER_NAME",
  "BIDDER_SHIPPING_REQUIRED",
]);
const ZERO_VAT_TOOLTIP = "VAT is 0% for this destination. Customs duties or import taxes may still be charged separately to the collector.";
const TAX_NOTICE_COPY = Object.freeze({
  LOCAL_TAX_MAY_APPLY_SELLER_RESPONSIBLE:
    "Taxes may apply. The seller is responsible for any applicable local taxes.",
  IMPORT_DUTIES_MAY_APPLY_ON_DELIVERY:
    "Import duties and taxes may apply on delivery.",
  TAX_COLLECTED_ON_BEHALF_OF_SELLER:
    "Tax included (collected on behalf of the seller).",
  REVERSE_CHARGE_EU_VAT:
    "VAT not charged. Reverse charge applies.",
  SELLER_RESPONSIBLE_TAX_NOT_COLLECTED:
    "Taxes may apply. This seller will handle any applicable taxes.",
});

function parsePriceValue(value) {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseVatRate(value) {
  if (value === null || value === undefined) {
    return null;
  }
  const numeric = typeof value === 'number' ? value : Number(String(value).trim());
  if (!Number.isFinite(numeric)) {
    return null;
  }
  return numeric > 0 ? numeric : null;
}

function formatVatRate(rate) {
  if (typeof rate !== 'number' || !Number.isFinite(rate)) {
    return null;
  }
  const percentage = rate * 100;
  const formatted = Number.isInteger(percentage)
    ? percentage.toString()
    : percentage.toFixed(2).replace(/\.?0+$/, '');
  return `${formatted}%`;
}

function roundMoney(value) {
  const numeric = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(numeric)) {
    return null;
  }
  return Number(numeric.toFixed(2));
}

function formatMoney(value, currency) {
  const rounded = roundMoney(value);
  if (!Number.isFinite(rounded)) {
    return null;
  }
  if (Number.isInteger(rounded)) {
    return NumberUtils.getArtworkPrice(rounded, currency);
  }
  return formatCurrencyAmount(rounded, currency, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) || null;
}

export default function BuyDetailsDesktop({
  artworkData,
  onBack,
  onFooterChange,
  registerCheckoutHandler,
  typeOfPurchase = 'standard',
  initialLimitedEditionSelection = null,
  initialPurchaseState = null,
}) {
  const locale = usePreferredLocale();
  const t = useTranslations('common.shared.artwork_view.buy_details');
  const navigate = useNavigate();
  const isLimitedEditionPurchase = typeOfPurchase === 'limited_edition';
  const [currentBuySection, setCurrentBuySection] = useState(
    isLimitedEditionPurchase ? 'size' : 'shipping'
  );

  const [formState, setFormState] = useState({
    limited_edition_size: null,
    limited_edition_frame: null,
    limited_edition_frame_price: 0,
    limited_edition_museum_label: null,
    limited_edition_museum_label_price: 0,
    shipping_details: null,
    billing_address: null,
    billing_info: null,
    personalized_message: '',
  });
  const initialPurchaseStateRef = useRef(initialPurchaseState);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [ownershipModalOpen, setOwnershipModalOpen] = useState(false);
  const [serverPricing, setServerPricing] = useState(null);
  const paymentProfile = usePaymentProfile();
  const ensurePaymentProfile = paymentProfile?.ensurePaymentProfile;
  const profileBillingAddress = paymentProfile?.profile?.billing_address ?? null;
  const profileBillingInfo = paymentProfile?.profile?.billing_info ?? null;
  const stripeInstanceRef = useRef(null);
  const activeOffer = resolveActiveViewerOffer(artworkData);
  const activeOfferPrice = parseOfferAmount(activeOffer?.offer_price);

  const resolveStripeInstance = useCallback(async () => {
    if (stripeInstanceRef.current) {
      return stripeInstanceRef.current;
    }
    const publishableKey = stripeConfig.publishableKey || await resolveStripePublishableKey();
    if (!publishableKey) {
      return null;
    }
    const instance = await loadStripe(publishableKey);
    stripeInstanceRef.current = instance;
    return instance;
  }, []);
  const {
    completePurchasePayment,
    dismissPurchasePayment,
    handlePaymentIntentResponse,
    purchasePayment,
  } = useArtworkPurchasePayment({ navigate, resolveStripeInstance });

  const promptProfileUpdate = useCallback(async (errorCode) => {
    setFormError({ element: 'purchase_error', error_code: errorCode });
    if (typeof ensurePaymentProfile !== "function") {
      return;
    }
    try {
      await ensurePaymentProfile({ forceSetup: true });
      setFormError(null);
    }
    catch (error) {
      if (error?.code === "BIDDER_PROFILE_CANCELLED") {
        return;
      }
      console.warn("[BuyDetailsDesktop] Unable to open payment profile modal", error);
    }
  }, [ensurePaymentProfile]);

  const purchaseDetails = getArtworkPurchaseDetails(artworkData);
  const currency = purchaseDetails.currency;
  const artworkPrice = purchaseDetails.price;
  const normalizedArtworkPrice = parsePriceValue(artworkPrice) ?? 0;
  const limitedEditionSizeKey = formState.limited_edition_size?.size ?? null;
  const shippingCountry = formState.shipping_details?.address_country ?? null;
  // When the limited edition flow is active we must drive pricing off the selected size.
  const selectedLimitedEditionPrice = parsePriceValue(
    formState.limited_edition_size?.size_data?.price,
  );
  const selectedFramePrice = parsePriceValue(formState.limited_edition_frame_price) ?? 0;
  const selectedMuseumLabelPrice = parsePriceValue(
    formState.limited_edition_museum_label_price,
  ) ?? 0;
  const limitedEditionSubtotal = (selectedLimitedEditionPrice ?? 0) + selectedFramePrice + selectedMuseumLabelPrice;
  const hasSelectedLimitedEditionSize = typeof selectedLimitedEditionPrice === 'number';
  const hasSelectedFrameOption = Boolean(formState.limited_edition_frame);
  const hasSelectedMuseumLabelOption = Boolean(formState.limited_edition_museum_label);
  // Treat the frame and museum label choices as part of the limited edition configuration so totals stay in sync.
  const hasCompleteLimitedEditionSelection = typeOfPurchase !== 'limited_edition'
    || (hasSelectedLimitedEditionSize && hasSelectedFrameOption && hasSelectedMuseumLabelOption);
  const basePrice = isLimitedEditionPurchase
    ? limitedEditionSubtotal
    : (activeOfferPrice ?? normalizedArtworkPrice);
  const frameSelectionId = formState.limited_edition_frame;
  const museumLabelSelectionId = formState.limited_edition_museum_label;

  const shipCfg = getArtworkShippingConfig(artworkData);
  const ownTier = shipCfg.artwork_shipping_own;
  const restTier = shipCfg.artwork_shipping_rest;
  const exceptions = shipCfg.artwork_shipping_exceptions;
  const {
    quote: limitedEditionShippingQuote,
  } = useLimitedEditionShippingQuote({
    country: shippingCountry,
    size: limitedEditionSizeKey,
    enabled: isLimitedEditionPurchase && Boolean(shippingCountry) && Boolean(limitedEditionSizeKey),
  });

  const shippingCost = useMemo(() => {
    if (isLimitedEditionPurchase) {
      if (!limitedEditionSizeKey || !shippingCountry) {
        return null;
      }
      if (!limitedEditionShippingQuote) {
        return null;
      }
      const amount = parsePriceValue(limitedEditionShippingQuote.amount);
      return Number.isFinite(amount) ? amount : 0;
    }

    const normaliseAmount = (value) => parsePriceValue(value) ?? 0;

    if (!formState.shipping_details) return 0;
    const dest = formState.shipping_details.address_country;
    const ex = exceptions.find((entry) => entry.location === dest);
    if (ex && ex.price !== undefined) return normaliseAmount(ex.price);
    if (dest === ownTier.location) return normaliseAmount(ownTier.price);
    return normaliseAmount(restTier.price);
  }, [
    exceptions,
    formState.shipping_details,
    limitedEditionShippingQuote,
    limitedEditionSizeKey,
    ownTier,
    restTier,
    shippingCountry,
    isLimitedEditionPurchase,
  ]);
  const shippingCurrency = isLimitedEditionPurchase
    ? (limitedEditionShippingQuote?.currency ?? currency)
    : currency;
  const sellerCountry = isLimitedEditionPurchase
    ? 'Spain'
    : purchaseDetails.galleryCountry;
  const buyerCountry = shippingCountry;
  const billingInfo = formState.billing_info ?? profileBillingInfo ?? null;
  const sellerTaxEnabled = purchaseDetails.galleryTaxEnabled;

  const normalizedServerPricing = useMemo(() => {
    if (!serverPricing) {
      return null;
    }
    const normalizedCurrency = serverPricing.currency || currency;
    const normalizeAmount = (value) => {
      const parsed = parsePriceValue(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    return {
      price: normalizeAmount(serverPricing.price),
      shipping: normalizeAmount(serverPricing.shipping),
      taxes: normalizeAmount(serverPricing.taxes),
      total: normalizeAmount(serverPricing.total),
      currency: normalizedCurrency,
      vatRate: parseVatRate(serverPricing.vat_rate),
      vatCountry: serverPricing.vat_country ?? null,
      taxMode: serverPricing.tax_mode ?? null,
      taxNoticeCode: serverPricing.tax_notice_code ?? null,
    };
  }, [currency, serverPricing]);

  useEffect(() => {
    setServerPricing(null);
  }, [basePrice, shippingCost, shippingCountry, billingInfo?.vat_id, billingInfo?.vat_valid, billingInfo?.is_business]);

  useEffect(() => {
    setFormState((prevState) => {
      if (prevState.billing_address || prevState.billing_info) {
        return prevState;
      }

      if (!profileBillingAddress && !profileBillingInfo) {
        return prevState;
      }

      return {
        ...prevState,
        billing_address: profileBillingAddress ?? null,
        billing_info: profileBillingInfo ?? null,
      };
    });
  }, [profileBillingAddress, profileBillingInfo]);

  const clientTaxContext = useMemo(() => {
    if (!buyerCountry || !sellerCountry) {
      return { taxMode: null, taxNoticeCode: null };
    }
    if (qualifiesForEuReverseCharge({
      buyerCountry,
      sellerCountry,
      billingInfo,
    })) {
      return {
        taxMode: TAX_MODES.REVERSE_CHARGE,
        taxNoticeCode: "REVERSE_CHARGE_EU_VAT",
      };
    }
    if (isLimitedEditionPurchase) {
      return {
        taxMode: TAX_MODES.PLATFORM_TAX_COLLECTED,
        taxNoticeCode: null,
      };
    }
    return resolveOriginalTaxContext({
      buyerCountry,
      sellerCountry,
      sellerTaxEnabled,
    });
  }, [billingInfo, buyerCountry, isLimitedEditionPurchase, sellerCountry, sellerTaxEnabled]);
  const effectiveTaxMode = normalizedServerPricing?.taxMode ?? clientTaxContext.taxMode;
  const effectiveTaxNoticeCode = normalizedServerPricing?.taxNoticeCode ?? clientTaxContext.taxNoticeCode;
  const isNoTaxMode = effectiveTaxMode === TAX_MODES.NO_TAX_COLLECTED_NOTICE;

  const fallbackVatRate = useMemo(() => {
    if (!buyerCountry || !sellerCountry || effectiveTaxMode === TAX_MODES.REVERSE_CHARGE) {
      return null;
    }
    return resolveVatRate({ buyerCountry, sellerCountry });
  }, [buyerCountry, effectiveTaxMode, sellerCountry]);
  const fallbackVatAmount = useMemo(() => {
    if (!buyerCountry || !sellerCountry || effectiveTaxMode === TAX_MODES.REVERSE_CHARGE) {
      return null;
    }
    return calculateVatAmount({
      price: basePrice,
      shipping: shippingCost ?? 0,
      buyerCountry,
      sellerCountry,
    });
  }, [basePrice, buyerCountry, effectiveTaxMode, sellerCountry, shippingCost]);
  const backendVatAmount = typeof normalizedServerPricing?.taxes === 'number'
    ? normalizedServerPricing.taxes
    : null;
  const vatAmountForDisplay = useMemo(() => {
    if (isNoTaxMode) {
      return 0;
    }
    if (backendVatAmount === null || backendVatAmount === undefined) {
      return fallbackVatAmount ?? null;
    }
    if (backendVatAmount <= 0 && typeof fallbackVatAmount === 'number' && fallbackVatAmount > 0) {
      return fallbackVatAmount;
    }
    return backendVatAmount;
  }, [backendVatAmount, fallbackVatAmount, isNoTaxMode]);
  const backendTotalAmount = typeof normalizedServerPricing?.total === 'number'
    ? normalizedServerPricing.total
    : null;
  const resolvedShippingCost = Number.isFinite(shippingCost) ? shippingCost : 0;
  const fallbackVatCost = fallbackVatAmount ?? 0;
  const fallbackTotalCost = roundMoney(basePrice + resolvedShippingCost + fallbackVatCost) ?? 0;

  const formattedPrice = useMemo(() => {
    if (isLimitedEditionPurchase) {
      if (typeof selectedLimitedEditionPrice !== 'number') {
        return '-';
      }

      return formatMoney(selectedLimitedEditionPrice, currency);
    }

    return formatMoney(artworkPrice, currency);
  }, [artworkPrice, currency, isLimitedEditionPurchase, selectedLimitedEditionPrice]);
  const formattedShipping = useMemo(() => {
    if (!Number.isFinite(shippingCost)) {
      return null;
    }
    return formatMoney(shippingCost, shippingCurrency);
  }, [shippingCost, shippingCurrency]);
  const serverCurrency = normalizedServerPricing?.currency ?? null;
  const formattedTotal = useMemo(() => {
    const expectedVat = vatAmountForDisplay ?? fallbackVatAmount ?? 0;
    if (backendTotalAmount !== null) {
      const expectedMinimum = basePrice + resolvedShippingCost + expectedVat;
      const useBackendTotal = backendTotalAmount >= expectedMinimum * 0.999;
      if (useBackendTotal) {
        return formatMoney(backendTotalAmount, serverCurrency ?? currency);
      }
    }
    if (isLimitedEditionPurchase && !hasSelectedLimitedEditionSize) {
      return '-';
    }

    return formatMoney(fallbackTotalCost, currency);
  }, [
    backendTotalAmount,
    currency,
    fallbackTotalCost,
    fallbackVatAmount,
    vatAmountForDisplay,
    hasSelectedLimitedEditionSize,
    isLimitedEditionPurchase,
    serverCurrency,
    basePrice,
    resolvedShippingCost,
  ]);
  const formattedVat = useMemo(() => {
    if (vatAmountForDisplay === null || vatAmountForDisplay === undefined) {
      return null;
    }

    const safeAmount = vatAmountForDisplay <= 0 ? 0 : vatAmountForDisplay;
    return formatMoney(safeAmount, serverCurrency ?? currency);
  }, [currency, serverCurrency, vatAmountForDisplay]);

  const selectionStepTotalDisplay = useMemo(() => {
    if (normalizedServerPricing && typeof normalizedServerPricing.price === 'number') {
      const vatAmount = typeof normalizedServerPricing.taxes === 'number'
        ? normalizedServerPricing.taxes
        : 0;
      return formatMoney(normalizedServerPricing.price + vatAmount, serverCurrency ?? currency);
    }

    if (!isLimitedEditionPurchase) {
      return formatMoney(basePrice + fallbackVatCost, currency);
    }

    if (!hasSelectedLimitedEditionSize) {
      return '-';
    }

    return formatMoney(basePrice + fallbackVatCost, currency);
  }, [
    basePrice,
    currency,
    fallbackVatCost,
    hasSelectedLimitedEditionSize,
    isLimitedEditionPurchase,
    normalizedServerPricing,
    serverCurrency,
  ]);
  const serverPriceAmount = typeof normalizedServerPricing?.price === 'number'
    ? normalizedServerPricing.price
    : null;
  const paymentPriceDisplay = useMemo(() => {
    if (!isLimitedEditionPurchase && serverPriceAmount !== null) {
      return formatMoney(serverPriceAmount, serverCurrency ?? currency);
    }
    return formattedPrice;
  }, [
    currency,
    formattedPrice,
    isLimitedEditionPurchase,
    serverCurrency,
    serverPriceAmount,
  ]);
  const backendVatRate = typeof normalizedServerPricing?.vatRate === 'number'
    ? normalizedServerPricing.vatRate
    : null;
  const effectiveVatRate = useMemo(() => {
    if (isNoTaxMode) {
      return 0;
    }
    if (typeof backendVatRate === 'number' && backendVatRate > 0) {
      return backendVatRate;
    }
    if (typeof fallbackVatRate === 'number' && fallbackVatRate > 0) {
      return fallbackVatRate;
    }
    return backendVatRate ?? fallbackVatRate ?? null;
  }, [backendVatRate, fallbackVatRate, isNoTaxMode]);
  const vatPercentageLabel = typeof effectiveVatRate === 'number'
    ? formatVatRate(effectiveVatRate)
    : null;
  const taxLabelBase = isLimitedEditionPurchase ? t('labels.vat') : t('labels.tax');
  const vatLabel = vatPercentageLabel ? `${taxLabelBase} (${vatPercentageLabel})` : taxLabelBase;
  const shouldShowZeroVatInfo = effectiveVatRate === 0 && vatAmountForDisplay !== null;
  const taxNoticeMessage = effectiveTaxNoticeCode
    ? (TAX_NOTICE_COPY[effectiveTaxNoticeCode] ?? null)
    : null;
  const vatInfoMessage = taxNoticeMessage ?? (shouldShowZeroVatInfo ? ZERO_VAT_TOOLTIP : null);

  const hasShippingDestination = Boolean(formState.shipping_details);
  const shippingReady = isLimitedEditionPurchase
    ? (
      hasShippingDestination
      && Boolean(limitedEditionSizeKey)
      && Boolean(limitedEditionShippingQuote)
    )
    : hasShippingDestination;
  const fallbackShippingDisplay = shippingReady && formattedShipping ? formattedShipping : '-';
  const shippingAmountFromServer = typeof normalizedServerPricing?.shipping === 'number'
    ? normalizedServerPricing.shipping
    : null;
  const shippingDisplay = normalizedServerPricing
    ? (
      shippingReady && Number.isFinite(shippingAmountFromServer ?? resolvedShippingCost)
        ? formatMoney(shippingAmountFromServer ?? resolvedShippingCost, serverCurrency ?? currency)
        : '-'
    )
    : fallbackShippingDisplay;
  const shouldShowTotal = hasCompleteLimitedEditionSelection && shippingReady;
  const totalDisplay = shouldShowTotal ? formattedTotal : '-';
  const isLimitedEditionSelectionStep = isLimitedEditionPurchase && currentBuySection === 'size';
  const localizedFooterButtonLabel = isLimitedEditionSelectionStep
    ? t('footer.continue')
    : t('footer.pay_now');
  const footerDisabled = isLimitedEditionSelectionStep
    ? !hasCompleteLimitedEditionSelection
    : (!shippingReady || !hasCompleteLimitedEditionSelection);
  const footerAmountValue = isLimitedEditionSelectionStep ? selectionStepTotalDisplay : totalDisplay;
  const frameDetails = useMemo(() => {
    if (!isLimitedEditionPurchase || !hasSelectedFrameOption) {
      return null;
    }

    if (!frameSelectionId || frameSelectionId === 'none' || selectedFramePrice <= 0) {
      return null;
    }

    const label = FRAME_OPTION_LABELS[frameSelectionId] ?? frameSelectionId;
    return {
      label: `${t('labels.frame')} (${label})`,
      value: `+${NumberUtils.getArtworkPrice(selectedFramePrice, currency)}`,
    };
  }, [currency, frameSelectionId, hasSelectedFrameOption, isLimitedEditionPurchase, selectedFramePrice, t]);

  const museumLabelDetails = useMemo(() => {
    if (!isLimitedEditionPurchase || !hasSelectedMuseumLabelOption) {
      return null;
    }

    if (!museumLabelSelectionId || museumLabelSelectionId === 'none' || selectedMuseumLabelPrice <= 0) {
      return null;
    }

    const label = MUSEUM_LABEL_OPTION_LABELS[museumLabelSelectionId] ?? museumLabelSelectionId;
    return {
      label: `${t('labels.museum_label')} (${label})`,
      value: `+${NumberUtils.getArtworkPrice(selectedMuseumLabelPrice, currency)}`,
    };
  }, [currency, hasSelectedMuseumLabelOption, isLimitedEditionPurchase, museumLabelSelectionId, selectedMuseumLabelPrice, t]);
  const navigateToProfile = (username) => {
    if (!username) {
      return;
    }

    if (artworkData?.slug) {
      trackArtworkPreferenceInteraction({
        slug: artworkData.slug,
        signal: ARTWORK_PREFERENCE_SIGNALS.GALLERY_PROFILE_VIEW_FROM_ARTWORK,
        metadata: { source: 'shipping-gallery-info-container' },
      });
    }

    navigate(`/user/${username}`);
  };

  const handleProfileKeyDown = (username) => (event) => {
    if (!username) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigate(`/user/${username}`);
    }
  };

  const artworkAbout = artworkData.artwork_about || {};
  const size = artworkAbout.artwork_size;
  const formattedSize = size ? `${size.length} x ${size.height} ${size.unit}` : '-';
  const artistName = getArtworkArtistDisplayName(artworkData);
  const artworkTitle = formatArtworkTitleForDisplay(artworkAbout.artwork_title) || '-';
  const artworkYear = artworkAbout.artwork_year || '-';
  const artworkMedium = getLocalizedArtworkMedium(artworkAbout.artwork_medium, locale);
  const galleryProfile = getArtworkPurchaseGalleryProfile(artworkData);
  const galleryUsername = galleryProfile.username;

  const continueToCheckout = useCallback(async () => {
    if (formLoading) {
      return;
    }

    setFormLoading(true);
    setFormError(null);

    try {
      const taxesAmount = Math.max(0, vatAmountForDisplay ?? fallbackVatAmount ?? 0);
      const shippingAmount = Number.isFinite(resolvedShippingCost) ? resolvedShippingCost : 0;
      const pricingPayload = {
        price: basePrice,
        shipping: shippingAmount,
        taxes: taxesAmount,
        total: basePrice + shippingAmount + taxesAmount,
        currency: serverCurrency ?? currency,
        shipping_currency: shippingCurrency ?? currency,
        vat_rate: effectiveVatRate ?? null,
        vat_country: buyerCountry ?? null,
        source: 'client',
      };
      const purchasePayload = {
        ...formState,
        pricing: pricingPayload,
        client_pricing: pricingPayload,
      };
      const { response, data } = await UserService.createArtworkPaymentIntent(
        artworkData._id,
        purchasePayload,
        typeOfPurchase
      );

      if (!response?.ok) {
        const errorCode = data?.error_type ?? data?.code ?? "PAYMENT_FAILED";
        if (PROFILE_ERROR_CODES.has(errorCode)) {
          await promptProfileUpdate(errorCode);
        }
        else {
          setFormError({ element: 'purchase_error', error_code: errorCode });
        }
        return;
      }

      setServerPricing(data?.pricing ?? null);
      await handlePaymentIntentResponse(data);
    }
    catch (error) {
      console.error("[BuyDetailsDesktop] Checkout failed", error);
      setFormError({ element: 'purchase_error', error_code: error?.code ?? 'PAYMENT_FAILED' });
    }
    finally {
      setFormLoading(false);
    }
  }, [
    artworkData._id,
    formLoading,
    formState,
    basePrice,
    buyerCountry,
    currency,
    effectiveVatRate,
    fallbackVatAmount,
    resolvedShippingCost,
    serverCurrency,
    shippingCurrency,
    vatAmountForDisplay,
    handlePaymentIntentResponse,
    promptProfileUpdate,
    typeOfPurchase,
  ]);
  const proceedToShipping = useCallback(() => {
    if (!hasCompleteLimitedEditionSelection) {
      return;
    }

    setCurrentBuySection('shipping');
  }, [hasCompleteLimitedEditionSelection]);

  const handleManagePaymentMethods = useCallback(() => {
    if (!artworkData?.slug) {
      navigate('/profile/settings/payment-methods');
      return;
    }

    saveArtworkPurchaseResume(artworkData._id, {
      artworkId: artworkData._id,
      purchaseType: typeOfPurchase,
      currentBuySection,
      formState,
    });
    navigate('/profile/settings/payment-methods', { state: { checkoutReturn: { useHistoryBack: true } } });
  }, [artworkData?.slug, currentBuySection, formState, navigate, typeOfPurchase]);

  // Reset the purchase wizard whenever the purchase type changes.
  useEffect(() => {
    const resumedPurchaseState = initialPurchaseStateRef.current;
    if (resumedPurchaseState?.formState) {
      setCurrentBuySection(resumedPurchaseState.currentBuySection ?? (isLimitedEditionPurchase ? 'size' : 'shipping'));
      setFormState(resumedPurchaseState.formState);
      initialPurchaseStateRef.current = null;
      return;
    }

    setCurrentBuySection(isLimitedEditionPurchase ? 'size' : 'shipping');
    setFormState({
      limited_edition_size: initialLimitedEditionSelection && initialLimitedEditionSelection.sizeKey
        ? {
          size: initialLimitedEditionSelection.sizeKey,
          size_data: initialLimitedEditionSelection.sizeData,
        }
        : null,
      limited_edition_frame: null,
      limited_edition_frame_price: 0,
      limited_edition_museum_label: null,
      limited_edition_museum_label_price: 0,
      shipping_details: null,
      billing_address: profileBillingAddress ?? null,
      billing_info: profileBillingInfo ?? null,
      personalized_message: '',
    });
  }, [initialLimitedEditionSelection, isLimitedEditionPurchase, profileBillingAddress, profileBillingInfo, typeOfPurchase]);

  useEffect(() => {
    if (isLimitedEditionPurchase && currentBuySection === 'size') {
      registerCheckoutHandler(proceedToShipping);
      return;
    }

    registerCheckoutHandler(continueToCheckout);
  }, [continueToCheckout, currentBuySection, isLimitedEditionPurchase, proceedToShipping, registerCheckoutHandler]);

  useEffect(() => {
    onFooterChange({
      amountValue: footerAmountValue,
      buttonLabel: localizedFooterButtonLabel,
      disabled: footerDisabled,
      loading: formLoading,
      offerPriceDetails: null,
      auxiliaryContent: (
        <span className="purchase-footer__desktop__aux-text">
          {t('footer.shipping_prefix')}: {shippingDisplay}
        </span>
      ),
    });
  }, [footerAmountValue, footerDisabled, formLoading, localizedFooterButtonLabel, onFooterChange, shippingDisplay, t]);

  return (
    <div className="buy-details-page__desktop">
      <button
        type="button"
        className="buy-details-page__desktop__back"
        onClick={() => {
          if (isLimitedEditionPurchase && currentBuySection === 'shipping') {
            setCurrentBuySection('size');
          } else {
            onBack();
          }
        }}
      >        <BackArrowIcon className="buy-details-page__desktop__back-icon" />
        <span>{t('footer.back')}</span>
      </button>

      <div className="buy-details-page__desktop__scroll remove-scrollbar">
        {isLimitedEditionPurchase && currentBuySection === 'size' ? (
          <div className="buy-details-page__desktop__artwork-details buy-details-page__desktop__artwork-details--limited-edition artwork-more-details__desktop__section">
            <h3 className="buy-details-page__desktop__limited-heading">{t('limited_edition.title')}</h3>
            <p className="buy-details-page__desktop__limited-copy">
              {t('limited_edition.bullets.archival_inkjet')}
            </p>
            <p className="buy-details-page__desktop__limited-copy">
              {t('limited_edition.bullets.produced_distributed')}
            </p>
            <p className="buy-details-page__desktop__limited-copy">
              {t('limited_edition.bullets.lowest_number')}
            </p>
            <p className="buy-details-page__desktop__limited-copy">
              {t('limited_edition.bullets.showcase_collection')}
            </p>
          </div>
        ) : null}

        <div className="buy-details-page__desktop__sections buy-details-page__sections">
          {isLimitedEditionPurchase && (
            <div
              className={`buy-details-page__desktop__panel${
                currentBuySection === 'shipping' ? ' buy-details-page__desktop__panel--hidden' : ''
              }`}
              aria-hidden={currentBuySection !== 'size'}
            >
              <SelectSize
                artworkData={artworkData}
                formState={formState}
                setFormState={setFormState}
                continueToShipping={proceedToShipping}
                typeOfPurchase={typeOfPurchase}
                showCertificate={false}
              />
            </div>
          )}

          <div
            className={`buy-details-page__desktop__panel${
              isLimitedEditionPurchase && currentBuySection === 'size'
                ? ' buy-details-page__desktop__panel--hidden'
                : ''
            }`}
            aria-hidden={isLimitedEditionPurchase && currentBuySection === 'size'}
          >
            {!isLimitedEditionPurchase && (
              <>
                <div className="buy-details-page__desktop__artwork-details artwork-more-details__desktop__section">
                  <div className="artwork-more-details__desktop__detail-row">
                    <span className="artwork-more-details__desktop__detail-label">{t('artwork_labels.artist')}</span>
                    <span className="artwork-more-details__desktop__detail-value">{artistName}</span>
                  </div>
                  <div className="artwork-more-details__desktop__detail-row">
                    <span className="artwork-more-details__desktop__detail-label">{t('artwork_labels.title')}</span>
                    <span className="artwork-more-details__desktop__detail-value">{artworkTitle}</span>
                  </div>
                  <div className="artwork-more-details__desktop__detail-row">
                    <span className="artwork-more-details__desktop__detail-label">{t('artwork_labels.type')}</span>
                    <span className="artwork-more-details__desktop__detail-value">{t('artwork_values.unique_work')}</span>
                  </div>
                  <div className="artwork-more-details__desktop__detail-row">
                    <span className="artwork-more-details__desktop__detail-label">{t('artwork_labels.medium')}</span>
                    <span className="artwork-more-details__desktop__detail-value">{artworkMedium}</span>
                  </div>
                  <div className="artwork-more-details__desktop__detail-row">
                    <span className="artwork-more-details__desktop__detail-label">{t('artwork_labels.dimensions')}</span>
                    <span className="artwork-more-details__desktop__detail-value">{formattedSize}</span>
                  </div>
                  <div className="artwork-more-details__desktop__detail-row">
                    <span className="artwork-more-details__desktop__detail-label">{t('artwork_labels.created_in')}</span>
                    <span className="artwork-more-details__desktop__detail-value">{artworkYear}</span>
                  </div>
                  <div className="artwork-more-details__desktop__detail-row">
                    <span className="artwork-more-details__desktop__detail-label">{t('artwork_labels.signature')}</span>
                    <span className="artwork-more-details__desktop__detail-value">{t('artwork_values.signature_hand_signed')}</span>
                  </div>
                  <div className="artwork-more-details__desktop__detail-row">
                    <span className="artwork-more-details__desktop__detail-label">{t('artwork_labels.price')}</span>
                    <span className="artwork-more-details__desktop__detail-value">
                      {activeOfferPrice !== null ? (
                        <ArtworkOfferPrice
                          originalPrice={artworkPrice}
                          offerPrice={activeOfferPrice}
                          currency={activeOffer?.currency ?? currency}
                        />
                      ) : (
                        formattedPrice
                      )}
                    </span>
                  </div>
                </div>
                <hr className="purchase-shipping-details__divider" />
              </>
            )}

            {/* Prioritize shipping form above gallery context so users confirm details before reading gallery info */}
            <ShippingDetails
              artworkData={artworkData}
              formState={formState}
              setFormState={setFormState}
              typeOfPurchase={typeOfPurchase}
            />

            <div className="purchase-shipping-block">
              <div className="shipping-gallery-info-container">
                {isLimitedEditionPurchase ? (
                  <img
                    src={SUARTE_WORDMARK_DATA_URL}
                    alt="Suarte"
                    className="shipping-suarte-logo"
                  />
                ) : (
                  <UserProfileImage
                    image={galleryProfile.imageId}
                    typeOfProfile="gallery"
                    className="shipping-gallery-image"
                    onClick={() => navigateToProfile(galleryUsername)}
                  />
                )}
                <div className="shipping-gallery-info">
                  <p
                    className="shipping-gallery-name"
                    role={isLimitedEditionPurchase ? undefined : 'button'}
                    tabIndex={isLimitedEditionPurchase ? undefined : 0}
                    onClick={isLimitedEditionPurchase ? undefined : () => navigateToProfile(galleryUsername)}
                    onKeyDown={isLimitedEditionPurchase ? undefined : handleProfileKeyDown(galleryUsername)}
                  >
                    {isLimitedEditionPurchase
                      ? t('shipping.suarte_name')
                      : galleryProfile.name}
                  </p>
                  <p className="shipping-from">
                    {isLimitedEditionPurchase
                      ? t('shipping.ships_from_alicante')
                      : t('shipping.ships_from_city_country', {
                        city: galleryProfile.city,
                        country: galleryProfile.country,
                      })}
                  </p>
                  <p className="shipping-timeline">
                    {isLimitedEditionPurchase
                      ? t('shipping.timeline_limited')
                      : t('shipping.timeline_standard')}
                  </p>
                </div>
              </div>
            </div>

            <hr className="purchase-shipping-details__divider" />

            <PurchasePaymentMethodPanel onManagePaymentMethods={handleManagePaymentMethods} />

            <hr className="purchase-shipping-details__divider" />

            <BillingDetailsCard
              billingAddress={formState.billing_address}
              billingInfo={formState.billing_info}
              shippingAddress={formState.shipping_details}
              onSaved={({ billingAddress, billingInfo }) => {
                setFormState((prevState) => ({
                  ...prevState,
                  billing_address: billingAddress,
                  billing_info: billingInfo,
                }));
              }}
            />

            <hr className="purchase-shipping-details__divider" />

            <PaymentDetails
              price={paymentPriceDisplay}
              offerDetails={
                activeOfferPrice !== null
                  ? {
                    originalPrice: artworkPrice,
                    offerPrice: activeOfferPrice,
                    currency: activeOffer?.currency ?? currency,
                  }
                  : null
              }
              frameDetails={frameDetails}
              museumLabelDetails={museumLabelDetails}
              shipping={shippingDisplay}
              vat={formattedVat}
              vatLabel={vatLabel}
              vatInfo={vatInfoMessage}
              total={totalDisplay}
            />

            <PersonalizedMessage
              formState={formState}
              setFormState={setFormState}
              typeOfPurchase={typeOfPurchase}
              artworkData={artworkData}
            />

            <FormError element="purchase_error" error={formError} />

            <ContinueButton
              loading={formLoading}
              turnOff={!shippingReady || !hasCompleteLimitedEditionSelection}
              className="purchase-shipping-details__submit font-size-medium"
              onClick={continueToCheckout}
            >
              {t('footer.pay_now')}
            </ContinueButton>

            <hr className="purchase-shipping-details__divider" />

            <div className="purchase-guarantee">
              <h3 className="purchase-protection__heading">{t('guarantee.heading')}</h3>
              <div className="guarantee__row">
                <span className="guarantee__text">{t('guarantee.secure_checkout')}</span>
              </div>
              <div className="guarantee__row">
                <span className="guarantee__text">
                  {t('guarantee.cancellation_policy')}
                </span>
              </div>
              <div className="guarantee__row">
                <span className="guarantee__text">{t('guarantee.authenticity')}</span>
              </div>
              <a
                href={resolveLocalizedPublicPath('/buyer-protection')}
                className="guarantee__learn-more"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('guarantee.learn_more')}
              </a>
            </div>

            <hr className="purchase-shipping-details__divider" />

            <div className="purchase-support">
              <h3 className="purchase-support__heading">{t('support.heading')}</h3>
              <p className="support__row">
                {t('support.call_or_whatsapp')}{' '}
                <a href="tel:+346281714101" className="support__link">
                  +34 628 171 410
                </a>
              </p>
              <p className="support__row">
                {t('support.send_email')}{' '}
                <a href="mailto:contact@suarte.art" className="support__link">
                  contact@suarte.art
                </a>
              </p>
            </div>
          </div>
        </div>

        {isLimitedEditionPurchase && currentBuySection === 'size' && (
          <div className="buy-details-page__desktop__certificate">
            <div className="purchase-select-size__certificate">
              <CertificateOfAuthenticityIcon className="purchase-select-size__certificate-icon" />
              <span className="purchase-select-size__certificate-text">
                Includes digital and physical Certificate of Authenticity issued by Suarte
              </span>
            </div>
          </div>
        )}
      </div>

      <PopupCertificateOfAuthenticity open={authModalOpen} onClose={() => setAuthModalOpen(false)} />
      <PopupCertificateOfOwnership open={ownershipModalOpen} onClose={() => setOwnershipModalOpen(false)} />
      {purchasePayment && (
        <PurchasePaymentModal
          clientSecret={purchasePayment.clientSecret}
          stripe={purchasePayment.stripe}
          title={t('footer.pay_now')}
          submitLabel={t('footer.pay_now')}
          onCancel={dismissPurchasePayment}
          onComplete={completePurchasePayment}
        />
      )}
    </div>
  );
}
